import DescriptionIcon from '@mui/icons-material/Description';
import { Link as MuiLink } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { EnvironmentDTO, WorkOrderReadDTO } from '../../api/dto';
import { getWorkOrders } from '../../api/work-orders';
import { useApplicationContext } from '../../components/application/ApplicationContextProvider';
import { FilterTypeEnum } from '../../components/searching/filters';
import { SearchTableWidget } from '../../components/SearchTableWidget';
import { fetchWorkOrdersById } from '../../lib/api';
import { getWorkOrderUrl } from '../../lib/information/workOrder';
import { getWorkOrderLabel } from '../../lib/label';
import { FetchUseCase } from '../fields/AsyncAutocompleteElement';
import {
    AdornableElement,
    AsyncAutocompleteFormElement,
    AutocompleteFormElement,
    FormElementCommon,
    TFilterElement,
    TFormElement,
} from '../types';
import { UiSchemaType } from '../UiSchemaType';

const getWorkOrderId = (workOrder: WorkOrderReadDTO) => workOrder.id;

const columnHelper = createColumnHelper<WorkOrderReadDTO>();
const columns = (intl: IntlShape, environment: EnvironmentDTO) => [
    columnHelper.accessor((row) => row.id, {
        id: 'id',
        header: intl.formatMessage({ id: 'workOrder.field.id' }),
        size: 20,
        cell: ({ getValue, row: { original } }) => (
            <MuiLink href={getWorkOrderUrl(environment, original.id)} target="_blank">
                {getValue()}
            </MuiLink>
        ),
    }),
    columnHelper.accessor((row) => row.description ?? '', {
        id: 'description',
        header: intl.formatMessage({ id: 'document.field.description' }),
        size: 80,
    }),
    columnHelper.accessor((row) => row.statusCodeDesc ?? '', {
        id: 'statusCodeDescription',
        header: intl.formatMessage({ id: 'document.field.status' }),
        size: 40,
    }),
    columnHelper.accessor((row) => row.mrcDescription ?? '', {
        id: 'mrcDescription',
        header: intl.formatMessage({ id: 'workOrder.field.mrc' }),
        size: 80,
    }),
];
const filterSchemas = ({ formatMessage }: IntlShape): TFilterElement[] => [
    {
        id: 'id',
        label: formatMessage({ id: 'workOrder.field.id' }),
        type: FilterTypeEnum.NUMBER,
    },
    {
        id: 'description',
        label: formatMessage({ id: 'document.field.description' }),
        type: FilterTypeEnum.STRING,
    },
    {
        id: 'mrcDescription',
        label: formatMessage({ id: 'workOrder.field.mrc' }),
        type: FilterTypeEnum.STRING,
    },
];

type WorkOrdersSearchTableDialogProps = Parameters<NonNullable<AdornableElement['endAdornment']>>[0];

const WorkOrdersSearchTableDialog: React.FC<WorkOrdersSearchTableDialogProps> = ({
    field,
    updateValue,
    multiple,
    matchId,
    disabled,
    readOnly,
}) => {
    const { environment } = useApplicationContext();
    const intl = useIntl();
    const { value } = field;
    const columnsMemo = useMemo(() => columns(intl, environment), [intl]);

    return (
        <SearchTableWidget
            title={intl.formatMessage({ id: 'activity.field.workOrders' })}
            disabled={disabled}
            readOnly={readOnly}
            selectedNodes={value ? (multiple ? value : [value]) : []}
            onSelect={(nodes: WorkOrderReadDTO[]) => {
                if (!nodes) return;
                if (nodes.length === 1 && !multiple) {
                    updateValue && updateValue(matchId ? getWorkOrderId(nodes[0]) : nodes[0]);
                } else {
                    updateValue && updateValue(matchId ? nodes.map((e) => getWorkOrderId(e)) : nodes);
                }
            }}
            columns={columnsMemo}
            filterSchemas={filterSchemas(intl)}
            getRowId={getWorkOrderId}
            pagedApiCall={getWorkOrders}
            adornmentIcon={<DescriptionIcon />}
        />
    );
};

const workOrdersTypeProperties = (): AsyncAutocompleteFormElement['typeProperties'] => ({
    fetchOptions: fetchWorkOrdersById,
    fetchUseCases: [FetchUseCase.ON_KEYSTROKE],
    getOptionLabel: getWorkOrderLabel,
    autocompleteProps: {
        disableCloseOnSelect: false,
        forcePopupIcon: false,
    },
    multiple: true,
    endAdornment: WorkOrdersSearchTableDialog,
});

interface WorkOrdersSchemaParameters
    extends Pick<AutocompleteFormElement, 'path' | 'label' | 'required'>,
        FormElementCommon {}

export const workOrdersSchema = ({
    path,
    label,
    required,
    disabled,
    readOnly,
}: WorkOrdersSchemaParameters): TFormElement => ({
    type: UiSchemaType.AUTOCOMPLETE_ASYNC,
    label,
    path,
    required,
    disabled,
    readOnly,
    typeProperties: workOrdersTypeProperties(),
});
